import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  displayName: "",
  pin: null,
  userID: null,
  email: null,
  paidOrganizer: false,
  paidPlayer: false,
  isAdmin: false,
  sport: null,
  token: null,
  accountID: null,
  profileData: {},
  isUnverified: false,
  build: 0,
  platform: null,
  requestBody: {},
  // SCO-2502: "url" once the boot/route sync wrote the paid flags from the
  // webview URL — consumers then treat Redux as authoritative over the stored
  // copy and the legacy `spr` override. Not persisted.
  entitlementsSource: null,
};

export const playerSlice = createSlice({
  name: "player",
  initialState,
  reducers: {
    setPlayer(state, action) {
      console.log("set player playerSlice action: ", action);
      const {
        displayName,
        pin,
        userid,
        userID,
        email,
        token,
        paidOrganizer,
        paidPlayer,
        isAdmin,
        sport,
        profileData,
        accountid,
        isUnverified,
        build,
        platform,
        requestBody,
      } = action.payload;
      state.displayName = displayName || state.displayName;
      state.userID = userid || userID || state.userID;
      // SCO-2834: an explicit empty pin (requestlogin "Valid, No Match on Token")
      // must clear the stored one — `pin || state.pin` kept a pin another device
      // had revoked, so login skipped the email-code step
      state.pin = "pin" in action.payload ? pin || null : state.pin;
      state.email = email || state.email;
      state.token = token || state.token;
      state.paidPlayer = paidPlayer || state.paidPlayer;
      state.paidOrganizer = paidOrganizer || state.paidOrganizer;
      state.isAdmin = isAdmin || state.isAdmin;
      state.sport = sport || state.sport;
      state.profileData = profileData || state.profileData;
      state.accountID = accountid || state.accountID;
      state.isUnverified = isUnverified || state.isUnverified;
      state.build = build || state.build;
      state.platform = platform || state.platform;
      state.requestBody = requestBody || state.requestBody;
      localStorage.setItem("player", JSON.stringify({ ...state }));
    },
    setPlayerNull(state, action) {
      console.log("setPlayerNull playerSlice action: ", action);
      const {
        displayName,
        pin,
        userid,
        userID,
        email,
        token,
        paidOrganizer,
        paidPlayer,
        isAdmin,
        sport,
        profileData,
        accountid,
        isUnverified,
      } = action.payload;
      state.displayName = displayName;
      state.userID = userid || userID;
      state.pin = pin;
      state.email = email;
      state.token = token;
      state.paidPlayer = paidPlayer;
      state.paidOrganizer = paidOrganizer;
      state.isAdmin = isAdmin;
      state.sport = sport;
      state.profileData = profileData;
      state.accountID = accountid;
      state.isUnverified = isUnverified;
      state.entitlementsSource = null;
      localStorage.setItem("player", JSON.stringify({ ...state }));
    },
    clearPlayer: () => initialState,
    // SCO-2495: URL-declared paid flags, written as-is (setPlayer is
    // sticky-true). Merges into the stored player rather than overwriting it —
    // this runs at boot, before any page rehydrates identity into Redux.
    setEntitlements(state, action) {
      const { paidPlayer, paidOrganizer } = action.payload || {};
      const patch = {};
      if (paidPlayer !== undefined) patch.paidPlayer = !!paidPlayer;
      if (paidOrganizer !== undefined) patch.paidOrganizer = !!paidOrganizer;
      Object.assign(state, patch);
      state.entitlementsSource = "url";
      // Only the declared flags touch the stored copy — an undeclared one must
      // not be clobbered by this webview's initial-state false — and only an
      // EXISTING plain-object copy is touched: nothing stale to correct means
      // no record invented on a logged-out device (several readers treat
      // presence of localStorage["player"] as meaningful), and a malformed
      // value is left for its existing readers rather than reshaped.
      try {
        const raw = localStorage.getItem("player");
        if (!raw) return;
        const stored = JSON.parse(raw);
        if (!stored || typeof stored !== "object" || Array.isArray(stored)) {
          return;
        }
        localStorage.setItem("player", JSON.stringify({ ...stored, ...patch }));
      } catch (e) {
        /* storage unavailable or unparsable — Redux still has the flags */
      }
    },
  },
});

export const { setPlayer, clearPlayer, setPlayerNull, setEntitlements } =
  playerSlice.actions;

export default playerSlice.reducer;
